import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, GripVertical, ToggleLeft, ToggleRight, ExternalLink } from 'lucide-react';
import { useAuth } from '../../App';
import { getSocials, createSocial, updateSocial, deleteSocial, toggleSocial } from '../../api/social';
import AdminLayout from './AdminLayout';

const socialPlatforms = [
  { name: 'Instagram', icon: 'instagram' },
  { name: 'TikTok', icon: 'tiktok' },
  { name: 'YouTube', icon: 'youtube' },
  { name: 'Pinterest', icon: 'pinterest' },
  { name: 'Facebook', icon: 'facebook' },
  { name: 'Twitter/X', icon: 'twitter' },
  { name: 'LinkedIn', icon: 'linkedin' },
];

const AdminSocial = () => {
  const { token } = useAuth();
  const [socials, setSocials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editSocial, setEditSocial] = useState(null);
  const [formData, setFormData] = useState({ platform: 'Instagram', url: '', icon: 'instagram', is_active: true, show_in_header: true, show_in_footer: true, order: 0 });

  const fetchSocials = async () => {
    try {
      const data = await getSocials();
      setSocials(data);
    } catch (err) {} finally { setLoading(false); }
  };

  useEffect(() => { fetchSocials(); }, []);

  const openModal = (social = null) => {
    if (social) {
      setEditSocial(social);
      setFormData({ ...social });
    } else {
      setEditSocial(null);
      setFormData({ platform: 'Instagram', url: '', icon: 'instagram', is_active: true, show_in_header: true, show_in_footer: true, order: socials.length + 1 });
    }
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editSocial) {
        await updateSocial(token, editSocial.id, formData);
      } else {
        await createSocial(token, formData);
      }
      setModalOpen(false);
      fetchSocials();
    } catch (err) { alert('Błąd zapisu'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Usunąć?')) return;
    try {
      await deleteSocial(token, id);
      fetchSocials();
    } catch (err) {}
  };

  const toggleActive = async (id) => {
    try {
      await toggleSocial(token, id);
      fetchSocials();
    } catch (err) {}
  };

  return (
    <AdminLayout title="Social Media">
      <div className="flex justify-between items-center mb-6">
        <p className="text-[#9CA3AF]">Zarządzaj ikonami social media w header i footer</p>
        <button onClick={() => openModal()} className="btn-primary" data-testid="add-social-btn">
          <Plus size={18} /> Dodaj
        </button>
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="spinner"></div></div> : (
        <div className="space-y-3">
          {socials.map((social) => (
            <div key={social.id} className="glass-card p-4 flex items-center justify-between" data-testid={`social-${social.id}`}>
              <div className="flex items-center gap-4">
                <div className="text-[#6B7280] cursor-grab"><GripVertical size={20} /></div>
                <div className="w-10 h-10 rounded-lg bg-[#10b981]/20 flex items-center justify-center text-[#10b981]">
                  {social.platform.charAt(0)}
                </div>
                <div>
                  <h3 className="text-white font-medium">{social.platform}</h3>
                  <p className="text-[#6B7280] text-sm truncate max-w-xs">{social.url || 'Brak URL'}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="hidden sm:flex items-center gap-2 text-xs">
                  <span className={`px-2 py-1 rounded ${social.show_in_header ? 'bg-[#10b981]/20 text-[#10b981]' : 'bg-[#6B7280]/20 text-[#6B7280]'}`}>Header</span>
                  <span className={`px-2 py-1 rounded ${social.show_in_footer ? 'bg-[#10b981]/20 text-[#10b981]' : 'bg-[#6B7280]/20 text-[#6B7280]'}`}>Footer</span>
                </div>
                <button onClick={() => toggleActive(social.id)} className={`text-2xl ${social.is_active ? 'text-[#10b981]' : 'text-[#6B7280]'}`}>
                  {social.is_active ? <ToggleRight /> : <ToggleLeft />}
                </button>
                <div className="flex gap-1">
                  {social.url && (
                    <a href={social.url} target="_blank" rel="noopener noreferrer" className="p-2 text-[#9CA3AF] hover:text-[#10b981]">
                      <ExternalLink size={18} />
                    </a>
                  )}
                  <button onClick={() => openModal(social)} className="p-2 text-[#9CA3AF] hover:text-[#10b981]">
                    <Edit size={18} />
                  </button>
                  <button onClick={() => handleDelete(social.id)} className="p-2 text-[#9CA3AF] hover:text-red-400">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-6 border-b border-white/5">
              <h2 className="text-xl font-bold text-white">{editSocial ? 'Edytuj' : 'Dodaj'} social media</h2>
              <button onClick={() => setModalOpen(false)} className="text-[#6B7280] hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Platforma</label>
                <select value={formData.platform} onChange={e => {
                  const p = socialPlatforms.find(sp => sp.name === e.target.value);
                  setFormData({...formData, platform: e.target.value, icon: p?.icon || 'link'});
                }} className="w-full">
                  {socialPlatforms.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">URL</label>
                <input type="url" value={formData.url} onChange={e => setFormData({...formData, url: e.target.value})} className="w-full" placeholder="https://instagram.com/..." />
              </div>
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} className="checkbox-custom" />
                  <span className="text-white">Aktywny</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.show_in_header} onChange={e => setFormData({...formData, show_in_header: e.target.checked})} className="checkbox-custom" />
                  <span className="text-white">Pokaż w header</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.show_in_footer} onChange={e => setFormData({...formData, show_in_footer: e.target.checked})} className="checkbox-custom" />
                  <span className="text-white">Pokaż w footer</span>
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setModalOpen(false)} className="btn-ghost flex-1">Anuluj</button>
                <button type="submit" className="btn-primary flex-1">Zapisz</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSocial;